import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject, inject, signal } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { filter } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MenuComponent } from './shared/component/menu/menu.component';
import { PreferencesService } from './preferences.service';
import { UnifiedAuthService } from './shared/services/unified-auth.service';
import { RoutesService } from './routes.service';
import { TourRealceComponent } from './shared/services/tour-mode/tour-realce.component';
import { TourService } from './shared/services/tour-mode/tour.service';
import { TourRoteiroService } from './shared/services/tour-mode/tour-roteiro.service';
import { rotaPermiteTour } from './shared/services/tour-mode/tour-rota-permitida';
import { TourGuiaComponent } from './shared/component/tour-guia/tour-guia.component';
import { ExpedienteModalComponent } from './shared/component/app-exmediente-modal/expediente-modal.component';
import { AiAssistantOverlayComponent } from './shared/component/ai-assistant/ai-assistant-overlay.component';
import { AiAgentService } from './shared/services/API/ai-agent.service';
import { GuiaOneService } from './shared/services/tour-mode/guia-one.service';
import { PushNotificationService } from './shared/services/push-notification.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { getDefaultLogo } from './shared/utils/logo.util';
import { AiRealceComponent } from './shared/services/ai-realce/ai-realce.component';
import { AiLayoutService } from './shared/services/ai-layout.service';
import { AiPainelComponent } from './shared/component/ai-assistant/ai-painel/ai-painel.component';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, CommonModule, MenuComponent, TourRealceComponent, TourGuiaComponent, ExpedienteModalComponent, AiAssistantOverlayComponent, AiRealceComponent, AiPainelComponent],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = (typeof window !== 'undefined' && window.location.hostname.includes('easyerp')) ? 'EasyERP' : 'ATSOne';
  teste = environment.urlApiPessoa;
  showMenu = false;
  showSearch = false;
  showFooter = false;
  isAdmin = false;
  marcaDagua = '';
  environment = environment;
  private intervalId: any;
  private routerSub!: Subscription;
  private swUpdateSub!: Subscription;
  private swPollSub!: Subscription;

  /** Motor do tour guiado (Task 4). O armazém de preferências é ligado no ngOnInit. */
  private readonly tourRoteiro = inject(TourRoteiroService);
  /**
   * O tour não existe em `pdv.`/`admin.` (spec §9: a travessia de hostname é da Fase 4).
   * Semeado pelo hostname para já valer no primeiro render, antes do primeiro NavigationEnd,
   * e reavaliado a cada navegação — a rota `/pdv` e `/admin` também contam, exatamente como
   * no irmão `ai-idle-help.service.ts`.
   */
  readonly tourPermitido = signal(
    rotaPermiteTour('', typeof window !== 'undefined' ? window.location.hostname : ''),
  );
  /**
   * D70: só injetado para o serviço NASCER CEDO — `GuiaOneService` decide a oferta de tour no
   * primeiro `NavigationEnd` autenticado (ver o construtor dele), e um serviço `providedIn:
   * 'root'` só existe de verdade a partir do primeiro `inject()`/uso. Sem este campo, nada no
   * app injeta `GuiaOneService` antes do orbe (Task 4) ser renderizado, e o primeiro
   * `NavigationEnd` da sessão passaria sem ninguém ouvindo. Nunca lido diretamente aqui.
   */
  private readonly guia = inject(GuiaOneService);

  /** Dono do modo/empurrão do assistente (T7) — o template lê `layout.acoplado()` para decidir
   * entre a casca acoplada (`ai-painel`) e o overlay/launcher. Campo público, igual a `aiAgent`. */
  public readonly layout = inject(AiLayoutService);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public authService: UnifiedAuthService,
    private routesService: RoutesService,
    private themePreferenceService: PreferencesService,
    public tour: TourService,
    private pushNotificationService: PushNotificationService,
    private appUpdateService: AppUpdateService,
    private swUpdate: SwUpdate,
    public aiAgent: AiAgentService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && this.swUpdate.isEnabled) {
      this.swUpdateSub = this.swUpdate.versionUpdates
        .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
        .subscribe(async () => {
          // Se autenticado, AppUpdateService exibe o modal de atualização
          if (this.authService.getUserInfos()) return;
          // Não autenticado: comportamento original (activate + reload, sem logout)
          await this.swUpdate.activateUpdate();
          window.location.reload();
        });

      // Verifica ativamente a cada 5 minutos (além da detecção passiva do SW)
      this.swPollSub = interval(5 * 60 * 1000).subscribe(() => this.swUpdate.checkForUpdate());
    }

    this.routerSub = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isPdv = hostname.startsWith('pdv.') || this.router.url.startsWith('/pdv');
        const isAdmin = hostname.startsWith('admin.') || this.router.url.startsWith('/admin');

        const currentRoute = this.getChildRoute(this.activatedRoute);
        this.showMenu = (currentRoute.snapshot.data?.['menu'] || false) && !isPdv && !isAdmin;
        // Review Task 7 (Importante 1): o empurrão do painel acoplado só existe dentro do shell
        // ERP (`.main-wrapper` do MenuComponent) — PDV e qualquer rota sem `data.menu` não têm
        // onde empurrar; sem isto o painel flutuava por cima dessas telas.
        this.layout.shellComMenu.set(this.showMenu);
        this.showSearch = currentRoute.snapshot.data?.['search'] || false;
        this.showFooter = currentRoute.snapshot.data?.['footer'] || false;
        this.isAdmin = isAdmin;
        this.tourPermitido.set(rotaPermiteTour(this.router.url, hostname));
        // D40 (revisão geral): sair de rota permitida com o tour ligado deixava a UI do tour
        // presa (era o scrim + lockScroll; hoje seriam o farol e o cartão do guia, que a rota
        // não permite). sair() persiste o índice — o convite de retomada volta ao regressar.
        if (!this.tourPermitido() && this.tourRoteiro.ativo()) void this.tourRoteiro.sair();
        this.aiAgent.verificarContextual(this.router.url);
      });

    this.marcaDagua = getDefaultLogo();
    this.themePreferenceService.loadTheme();
    this.themePreferenceService.loadCorSistema();

    // O motor depende só de get/setTourState + persistirTourAgora; a subclasse concreta
    // de PreferencesBaseService usada pelo ERP (PreferencesService) satisfaz esse contrato.
    // Ligar a referência aqui é seguro mesmo antes da hidratação: getTourState() é lido
    // sob demanda (GuiaOneService.avaliarOferta, D70), nunca no momento desta atribuição.
    this.tourRoteiro.prefs = this.themePreferenceService;

    const userResponse = this.authService.getUserInfos();
    if (userResponse?.usuarioPermissionStruct?.listSubPerfilStruct?.length > 0) {
      this.routesService.resetRoutes(userResponse);

      // Re-navega para a URL atual para que as rotas dinâmicas sejam avaliadas
      // Usa window.location.pathname porque this.router.url pode ainda ser '/'
      // no momento do ngOnInit (antes da navegação inicial completar)
      if (typeof window !== 'undefined') {
        const intendedUrl = window.location.pathname + window.location.search;
        if (intendedUrl !== '/' && intendedUrl !== '/home') {
          this.router.navigateByUrl(intendedUrl);
        }
      }

      this.pushNotificationService.init();
      this.pushNotificationService.refreshHistory();
    } else if (typeof window !== 'undefined') {
      // Usuário NÃO autenticado acessando uma rota dinâmica que não existe na config estática
      // (ex: /pessoa, /clientes) → redireciona para login para evitar tela branca
      // Rotas públicas (sem auth) são excluídas do redirect
      const intendedUrl = window.location.pathname;
      const publicPaths = ['/change-password', '/password-recovery', '/documento/', '/file/', '/criar-conta'];
      const isPublicPath = intendedUrl === '/' || publicPaths.some(p => intendedUrl.startsWith(p));
      if (!isPublicPath && !this.authService.isAuthenticated()) {
        this.router.navigate(['/']);
      }
    }

  }

  // --- LIMPEZA IMPORTANTE ---
  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.routerSub?.unsubscribe();
    this.swUpdateSub?.unsubscribe();
    this.swPollSub?.unsubscribe();
  }

  toggleTheme() {
    this.themePreferenceService.toggleTheme();
  }

  private getChildRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }


}
